// Split out of src/components/Wizard.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Wizard.jsx
// re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* ----------------------------- setup wizard --------------------------------- */


export const STEP4_PARTS = 2;

export function WizardProgress({ step, subStep, subSteps, steps }) {
  // subStep (journey review, Aug 2026): step 4 is really three screens — without naming
  // the part, the bar appears stuck for three taps and the "how much is left" question
  // the bar exists to answer goes unanswered exactly where the flow is longest.
  //
  // subSteps is now passed in rather than assumed (Alan, 30 Aug 2026): step 5 walks six
  // Learn & Earn parts while step 4 walks two, so a hardcoded STEP4_PARTS would have told
  // a parent on the fourth of six that they were on "part 4 of 2".
  const total = subSteps || STEP4_PARTS;
  // `steps` (LB-127): the auto route has five stops, not six — its step 4 is the review and
  // there is no step 5 — so the bar takes its stops from the route. Ids stay the same
  // numbers in both routes (the funnel event keys on them); the POSITION is what's shown.
  const list = steps || WIZARD_STEPS;
  const position = Math.max(1, list.findIndex((s) => s.id === step) + 1);
  // PIN before Notifications (Alan, 20 Sep): the ids no longer run in the order a parent
  // walks them (the PIN keeps id 2 but comes fifth), so "done" is decided by POSITION in
  // the route's list, never by comparing ids. An id comparison ticked the PIN from step 3
  // onwards and left the three steps before it unticked while on it.
  const currentIndex = position - 1;
  return (
    // Entry-surfaces pass (Sep 2026, approved sketch): the progress is a row of numbered
    // discs on the canvas — teal tick when done, navy for the current step — with a caption
    // naming the step. No card, no tracked caps, no emoji. The "part N of M" text stays in
    // the caption because specs read it.
    <div data-testid="wizard-progress" className="wizard-progress">
      <div className="flex items-center gap-1.5 px-0.5">
        {list.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === currentIndex ? "bg-slate-900 text-white" : i < currentIndex ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-400"}`} aria-label={`${s.label}${i < currentIndex ? ", done" : i === currentIndex ? ", current" : ""}`} data-step-key={s.key} data-step-state={i < currentIndex ? "done" : i === currentIndex ? "current" : "todo"}>
              {i < currentIndex ? <Icon id="ic-celebrate" size={14} style={{ color: "currentColor" }} /> : i + 1}
            </div>
            {i < list.length - 1 && <div className={`flex-1 h-0.5 ${i < currentIndex ? "bg-teal-300" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>
      <div className="text-xs font-medium text-slate-500 mt-2 px-0.5">Step {position} of {list.length} · {list[position - 1]?.label}{subStep ? ` · part ${subStep} of ${total}` : ""}</div>
    </div>
  );
}


// One-line explainer row that expands on tap (tokens step, journey review). The whole
// row is the button; "What's this?" names the affordance so the chevron isn't doing the
// signalling alone.
export function CollapsibleInfoRow({ icon, iconBg, title, pill, body, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-2" data-testid={testId}>
      <button type="button" className="w-full flex items-center gap-3 p-3.5 text-left" onClick={() => setOpen((o) => !o)}>
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>{icon}</div>
        <div className="min-w-0 flex-1 text-sm font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
          {title}
          {pill && <span className="text-xs font-medium bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{pill}</span>}
        </div>
        <span className="text-xs font-bold text-teal-600 flex-shrink-0 flex items-center gap-1">
          {ct("wizard.common.whats_this", "What's this?")}
          <Icon id="ic-forward" size={14} className={`inline-block transition-transform ${open ? "rotate-90" : ""}`} style={{ color: "currentColor" }} />
        </span>
      </button>
      {open && (
        <p className="text-sm text-slate-500 leading-snug px-3.5 pb-3.5 -mt-1">{body}</p>
      )}
    </div>
  );
}

export function WizardIntro({ emoji, icon, title, body, header, eyebrow, context }) {
  return (
    <div className="wizard-intro">
      {header ? <div className="mb-3">{header}</div> : null}
      {eyebrow ? <div className="text-xs font-semibold text-teal-700 mb-1 px-0.5">{eyebrow}</div> : null}
      <div className="flex items-center gap-2.5 mb-1 px-0.5">
        {icon ? <span className="flex-shrink-0 inline-flex items-center">{icon}</span> : null}
        <div className="text-lg leading-7 font-extrabold text-slate-900">{title}</div>
      </div>
      <p className="text-base text-slate-500 px-0.5">{body}</p>
      {context ? <div className="wizard-context mt-4 pt-3 border-t border-slate-200 px-0.5">{context}</div> : null}
    </div>
  );
}


export function WizardFooter({ children }) {
  return <div className="wizard-footer flex items-center gap-2 pb-2 pt-1">{children}</div>;
}

// Back control for the wizard (Alan, Aug 2026). Six steps with no way to correct a mistake
// was a real gap — and "start over" isn't an option once a PIN and children exist.
//
// Deliberately NOT wired into WizardProgress, even though that already renders ✓ marks for
// completed steps and would be the obvious next move. Making those tappable multiplies the
// entry paths into every step, and the PIN hazard below applies to all of them. A linear
// Back is the version that's safe to reason about.
export function WizardBackButton({ onBack }) {
  if (!onBack) return null;
  return (
    <button
      data-testid="wizard-back"
      className="flex-shrink-0 bg-pill text-navy font-bold text-sm rounded-lg py-2.5 px-4 md:bg-transparent md:text-slate-600 md:px-3 md:mr-auto"
      onClick={onBack}
      aria-label={ct("wizard.common.back", "Back")}
    >
      ← {ct("wizard.common.back", "Back")}
    </button>
  );
}

const CAP_PRESETS = [2, 3, 5, 10];

/* --------------------------- pull-to-refresh -------------------------------- */

// A custom pull-to-refresh gesture: needed because installed/standalone PWAs lose the
// browser's native pull-to-refresh (that gesture belongs to the browser chrome, which
// isn't present once the app runs standalone). Only activates when the page is already
// scrolled to the very top, so it never fights with normal scrolling.

/* Step identity (Alan, 20 Sep). Two things name a step, and they are kept apart on purpose:
 *
 * - `id` is the number saved in `state.onboardingStep`. It never changes meaning, so a
 *   household that is half-way through setup never needs renumbering. Only the wizard reads it.
 * - `key` is the stable NAME everything outside the wizard uses: the wizard_step_reached
 *   funnel event, the screen name for analytics, the admin panel. The funnel used to key on
 *   the number, and moving the PIN would have made "step 2" mean the PIN before the release
 *   and nothing in particular after it.
 *
 * The lists are in the order a parent WALKS them. The PIN moved to just before
 * Notifications on 20 Sep (Alan: parents see what the app does before being asked for a
 * PIN; setup still cannot finish without one). WizardProgress reads position from these
 * lists, so the order here is the order shown. */
export const WIZARD_STEP_KEY = { 1: "children", 2: "pin", 3: "money", 4: "earning", 5: "learnearn", 6: "notifications" };
const STEP_CHILDREN = { id: 1, key: "children", iconId: "ic-children", label: "Children" };
const STEP_PIN = { id: 2, key: "pin", iconId: "ic-pin", label: "Parent PIN" };
const STEP_MONEY = { id: 3, key: "money", iconId: "ic-banknote", label: "Token Setup" };
const STEP_NOTIFICATIONS = { id: 6, key: "notifications", iconId: "ic-bell", label: "Notifications" };
export const WIZARD_STEPS = [
  // Emoji sweep (28 Aug): structural surfaces use sprite icons; emoji stays celebratory.
  STEP_CHILDREN,
  STEP_MONEY,
  { id: 4, key: "earning", icon: true, label: "Habits & Earning" },
  { id: 5, key: "learnearn", iconId: "ic-brain", label: "Learn & Earn" },
  STEP_PIN,
  STEP_NOTIFICATIONS,
];
// The auto route (LB-128): steps 4 and 5 collapse into one review stop. Same ids — the
// review IS step 4 in state — so a route switch never renumbers anything.
export const WIZARD_STEPS_AUTO = [
  STEP_CHILDREN,
  STEP_MONEY,
  { id: 4, key: "earning", iconId: "ic-choice", label: "Your setup" },
  STEP_PIN,
  STEP_NOTIFICATIONS,
];

export { CAP_PRESETS };
